import { useRef, useState } from "react";
import { toast } from "sonner";
import { api, apiErr, mediaUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Palette, ImageUp, Loader2, Building2 } from "lucide-react";

const PRESET = ["#0F172A", "#2563EB", "#0E7490", "#047857", "#7C3AED", "#B91C1C", "#EA580C", "#111827"];

export default function BrandingCard() {
  const { org, refresh } = useAuth();
  const [color, setColor] = useState(org?.brand_color || "#0F172A");
  const [footer, setFooter] = useState(org?.report_footer || "");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const logoRef = useRef(null);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/organization", { brand_color: color, report_footer: footer });
      await refresh();
      toast.success("Branding saved");
    } catch (err) { toast.error(apiErr(err)); }
    finally { setSaving(false); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/organization/logo", fd);
      await refresh();
      toast.success("Logo uploaded — it will appear on exported PDFs");
    } catch (err) { toast.error(apiErr(err)); }
    finally { setUploading(false); }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="branding-card">
      <div className="flex items-center gap-2">
        <Palette className="h-5 w-5 text-blue-600" />
        <h2 className="font-display text-lg font-bold uppercase tracking-tight text-slate-900">Report Branding</h2>
      </div>
      <p className="mt-1 text-sm text-slate-500">Add your company logo and brand color to every exported PDF report.</p>

      <div className="mt-5 grid gap-6 md:grid-cols-2">
        {/* Logo */}
        <div>
          <label className="mb-2 block text-sm font-semibold text-slate-700">Company logo</label>
          <div className="flex items-center gap-4">
            <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
              {org?.logo_media_id
                ? <img data-testid="org-logo-preview" src={mediaUrl(org.logo_media_id)} alt="logo" className="h-full w-full object-contain" />
                : <Building2 className="h-8 w-8 text-slate-300" />}
            </div>
            <input ref={logoRef} type="file" accept="image/*" className="hidden" data-testid="logo-file-input"
              onChange={(e) => { uploadLogo(e.target.files[0]); e.target.value = ""; }} />
            <button data-testid="upload-logo-btn" onClick={() => logoRef.current?.click()} disabled={uploading}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageUp className="h-4 w-4" />}
              {uploading ? "Uploading…" : "Upload logo"}
            </button>
          </div>
        </div>

        {/* Color */}
        <div>
          <label className="mb-2 block text-sm font-semibold text-slate-700">Brand color</label>
          <div className="flex items-center gap-3">
            <input type="color" data-testid="brand-color-input" value={color} onChange={(e) => setColor(e.target.value)}
              className="h-11 w-14 cursor-pointer rounded-lg border border-slate-300 bg-white p-1" />
            <span className="font-mono text-sm uppercase text-slate-600">{color}</span>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {PRESET.map((c) => (
              <button key={c} data-testid={`preset-color-${c.replace("#", "")}`} onClick={() => setColor(c)}
                style={{ backgroundColor: c }} className={`h-7 w-7 rounded-md ring-2 ${color.toLowerCase() === c.toLowerCase() ? "ring-blue-400 ring-offset-2" : "ring-transparent"}`} />
            ))}
          </div>
        </div>
      </div>

      <div className="mt-5">
        <label className="mb-1 block text-sm font-semibold text-slate-700">Report footer note (optional)</label>
        <input data-testid="report-footer-input" value={footer} onChange={(e) => setFooter(e.target.value)}
          placeholder="Prepared by Summit Property Management · confidential" className="w-full rounded-lg border border-slate-300 px-4 py-3 text-sm outline-none focus:border-blue-600" />
      </div>

      <button data-testid="save-branding-btn" onClick={save} disabled={saving}
        className="mt-5 inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-3 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Palette className="h-4 w-4" />} Save branding
      </button>
    </div>
  );
}
